/**
 * Deferred-commit delete for Message Requests (#3039).
 *
 * Deleting takes the row out and drops the count at once, then holds the
 * request itself for the length of the undo window. The send happens when the
 * window closes, not when the row goes, so an undone delete was never sent and
 * needs no compensating call.
 *
 * One delete is pending at a time. Starting a second commits the first, which
 * is what a single snackbar implies.
 *
 * A request deleted from its open conversation may have no row in the list (the
 * overlay was never opened), so an entry carries its own count and an undo can
 * restore the count alone.
 *
 * Drives the list through window.PmRequests (pm/requests.js).
 */
(function () {
  'use strict';

  var UNDO_WINDOW_MS = 5000;

  // Ids per call to the batch decline endpoint. Longer runs are chunked.
  var BATCH_MAX = 100;

  var pending = null;

  // Entries whose decline is on the wire. The window has closed, so they are
  // no longer undoable, but the server still counts them and the list still
  // has no row for them. Both stay true until the call lands.
  var inFlightEntries = [];

  function api() {
    return window.PmRequests || null;
  }

  function t(key, fallback, params) {
    if (typeof window.t === 'function') {
      var s = window.t(key, params);
      if (s && s !== key) return s;
    }
    return fallback;
  }

  /**
   * Remove a row and keep what putting it back needs. The neighbours are read
   * before the removal, because focus has to land on a row that is still in the
   * list once this one is gone.
   */
  function captureRow(threadId) {
    var a = api();
    if (!a) return null;
    var row = a.row(threadId);
    if (!row) return null;

    var next = row.nextElementSibling;
    var prev = row.previousElementSibling;
    if (next && !next.classList.contains('message-request-row')) next = null;
    if (prev && !prev.classList.contains('message-request-row')) prev = null;

    var token = a.detachRow(threadId);
    if (!token) return null;

    return { id: String(threadId), token: token, next: next, prev: prev };
  }

  /**
   * Put captured rows back where they belong. The list places each one by its
   * own keyset cursor, so the order the batch is walked in does not matter.
   */
  function restoreRows(captures) {
    var a = api();
    if (!a) return 0;
    var restored = 0;
    for (var i = 0; i < captures.length; i++) {
      if (a.restoreRow(captures[i].token)) restored++;
    }
    if (restored) a.refreshEmptyState();
    return restored;
  }

  /**
   * Put a count back. The count is restored whether or not the rows could be
   * re-inserted: an overlay closed since the delete has no list to take them,
   * and the banner still has to report the request the server still holds.
   */
  function restoreCount(n) {
    var a = api();
    if (a && n > 0) a.setCount(a.getCount() + n);
  }

  /**
   * Put rows and their count back. With a list on screen the count follows the
   * rows that went back, because a row the list already holds was re-rendered
   * from the server, which counted it too. Rows the entry never captured, and
   * every row when no list is rendered, have nowhere to land, so their count
   * is restored on its own.
   */
  function restore(captures, count) {
    var a = api();
    if (!a) return;
    if (!a.list()) {
      restoreCount(count);
      return;
    }
    var placed = restoreRows(captures);
    var countOnly = Math.max(0, count - captures.length);
    restoreCount(placed + countOnly);
    // A count with no row behind it leaves the header reading higher than the
    // list it sits over. Only the server can render that row, so say the list
    // is behind and let the settled handler refetch it.
    if (countOnly > 0 || placed < captures.length) announceStale();
  }

  /** Say the rendered list is behind the server, so a later pass refetches it. */
  function announceStale() {
    try {
      document.dispatchEvent(new CustomEvent('pm:requests-overlay-stale'));
    } catch (e) { /* CustomEvent unavailable: nothing downstream is required */ }
  }

  /** Put a whole entry back. */
  function restoreEntry(entry) {
    restore(entry.captures, entry.count);
  }

  /** Put part of an entry back, after a chunk of its batch failed. */
  function restorePart(captures) {
    restore(captures, captures.length);
  }

  /**
   * Report a failed commit once per entry. A long batch is several calls, and
   * one user action deserves one message.
   */
  function reportFailure(entry) {
    if (entry.reported) return;
    entry.reported = true;
    var a = api();
    if (a) a.actionError();
  }

  function declineOptions(entry, body) {
    var init = {};
    if (body) init.body = body;
    // keepalive lets the send outlive the page. ApiClient reads the JWT
    // synchronously, so no token fetch has to complete during unload.
    if (entry.keepalive) init.keepalive = true;
    return init;
  }

  /**
   * Whether a failed decline is worth undoing. 403 on the single endpoint is
   * the server saying this thread is not a pending request, so there is
   * nothing to put back. The batch endpoint refuses the whole call instead,
   * which says nothing about any one request in it, so those rows come back.
   *
   * `silent` marks a send made as the page goes away, where there is no page
   * left to restore a row to or report an error on.
   */
  function shouldRestore(entry, err) {
    if (entry.silent) return false;
    if (!err) return true;
    return entry.bulk || err.status !== 403;
  }

  function sendSingle(entry) {
    var a = api();
    return a.apiPost('/thread/' + entry.ids[0] + '/decline', declineOptions(entry))
      .catch(function (err) {
        if (!shouldRestore(entry, err)) {
          // A swallowed 403 also covers the split turning off underneath the
          // page, where the request is still pending. The banner refetch then
          // counts a row this list no longer shows, so ask for a refetch.
          if (!entry.silent) announceStale();
          return;
        }
        restoreEntry(entry);
        reportFailure(entry);
      });
  }

  function sendBatch(entry) {
    var a = api();
    var sends = [];
    for (var start = 0; start < entry.ids.length; start += BATCH_MAX) {
      (function (chunk) {
        // Form-encoded with repeated keys: public/api/index.php JSON-decodes
        // only PUT/PATCH/DELETE, so a JSON body reaches a POST action empty.
        var body = new URLSearchParams();
        for (var i = 0; i < chunk.length; i++) body.append('thread_ids[]', chunk[i]);

        // Only a failed call restores anything. An id the server skips was
        // accepted or removed elsewhere, so its row is correctly gone; the
        // count that skip leaves too low is corrected by the banner refetch
        // pm/requests-live.js runs on pm:requests-undo-settled.
        sends.push(a.apiPost('/thread/decline-batch', declineOptions(entry, body))
          .catch(function (err) {
            if (!shouldRestore(entry, err)) return;
            restorePart(entry.captures.filter(function (c) { return chunk.indexOf(c.id) !== -1; }));
            releaseChunk(entry, chunk);
            reportFailure(entry);
          }));
      })(entry.ids.slice(start, start + BATCH_MAX));
    }
    return Promise.all(sends);
  }

  /**
   * A restored chunk is no longer held: its rows are back on screen and the
   * server never took them. The entry stays alive for the chunks still out.
   */
  function releaseChunk(entry, chunk) {
    for (var i = 0; i < chunk.length; i++) {
      var at = entry.ids.indexOf(chunk[i]);
      if (at !== -1) entry.ids.splice(at, 1);
    }
    entry.count = Math.max(0, entry.count - chunk.length);
  }

  function send(entry) {
    if (!api()) return Promise.resolve();
    return entry.bulk ? sendBatch(entry) : sendSingle(entry);
  }

  /**
   * Send, holding the list for as long as the call runs. A refresh that lands
   * inside the round trip would otherwise render the row back from a server
   * that has not applied the decline yet.
   */
  function flush(entry) {
    inFlightEntries.push(entry);
    // Released on both outcomes. An entry left in the list holds the rendered
    // list against every later refresh, for the life of the page.
    var release = function () {
      var i = inFlightEntries.indexOf(entry);
      if (i !== -1) inFlightEntries.splice(i, 1);
    };
    return send(entry).then(release, release);
  }

  function inFlightCount() {
    var n = 0;
    for (var i = 0; i < inFlightEntries.length; i++) n += inFlightEntries[i].count;
    return n;
  }

  // The displayed count is optimistic while a window is open, and the list
  // holds rows a refresh would drop, so pm/requests-live.js waits for this.
  function announceSettled(undone) {
    try {
      document.dispatchEvent(new CustomEvent('pm:requests-undo-settled', {
        detail: { undone: !!undone }
      }));
    } catch (e) { /* CustomEvent unavailable: nothing downstream is required */ }
  }

  /** End the window exactly once, either by sending or by putting the rows back. */
  function settle(entry, undone) {
    if (entry.settled) return;
    entry.settled = true;
    if (pending === entry) pending = null;

    if (entry.toast && typeof entry.toast.dismiss === 'function') entry.toast.dismiss();

    if (undone) {
      restoreEntry(entry);
      announceSettled(true);
      return;
    }

    // Announced after the decline lands, not when it is issued: a refresh that
    // the server answers before the decline commits renders the row back.
    flush(entry).then(function () { announceSettled(false); });
  }

  function start(entry) {
    // The toast is the way back. Without it there is no undo to hold the
    // request for, and deferring the send would only hide the delete.
    if (typeof window.showActionToast !== 'function') {
      entry.settled = true;
      flush(entry).then(function () { announceSettled(false); });
      return;
    }

    pending = entry;
    // One clock, and the toast owns it. Its onClose commits, and it is the
    // clock a user can hold open by pointing at the toast or tabbing into it.
    // A second timer here would commit underneath them while they read.
    entry.toast = window.showActionToast(entry.message, {
      actionLabel: t('common.pm.requests_undo', 'Undo'),
      duration: UNDO_WINDOW_MS,
      anchor: document.getElementById('threads_left_container'),
      focusAction: !!entry.fromKeyboard,
      onAction: function () { settle(entry, true); },
      onClose: function (acted) {
        if (!acted) settle(entry, false);
        // The keyboard was lent to the action button. Give it back to the list.
        if (!entry.fromKeyboard) return;
        var back = entry.focusReturn;
        if (back && back.isConnected && typeof back.focus === 'function') back.focus();
      }
    });
  }

  function afterRemoval(entry) {
    var a = api();
    // A request whose conversation is open leaves that pane too, so no
    // decision bar survives for a request that is on its way out.
    if (typeof a.closeOpenRequests === 'function') a.closeOpenRequests(entry.ids);
    a.setCount(Math.max(0, a.getCount() - entry.count));
    a.markChanged();
    if (entry.captures.length) a.refreshEmptyState();
    // Only a keyboard delete moves the focus. The row actions reveal on
    // focus-within, so doing it after a click arms the next row's Delete under
    // a cursor that has not moved.
    if (!entry.fromKeyboard) return;
    // Neighbours are read in list order, not selection order, so the keyboard
    // lands next to where the block of rows was. A delete from the conversation
    // captured no row, and focusAfterRemoval falls back for it.
    var last = entry.captures.length ? entry.captures[entry.captures.length - 1].next : null;
    var first = entry.captures.length ? entry.captures[0].prev : null;
    entry.focusReturn = a.focusAfterRemoval(last, first);
  }

  function bulkMessage(count) {
    if (typeof window.tPlural === 'function') {
      var s = window.tPlural('common.pm.requests_deleted_undo_bulk', count);
      if (s && s !== 'common.pm.requests_deleted_undo_bulk') return s;
    }
    return count + ' requests deleted';
  }

  function singleMessage(name) {
    // A request opened by direct link has no row to read a name from, and
    // "Request from  deleted" is not a sentence.
    if (!name) return bulkMessage(1);
    return t('common.pm.requests_deleted_undo', 'Request from ' + name + ' deleted', { name: name });
  }

  window.PmRequestsUndo = {
    /** Delete one request, undoably. Takes the row element. */
    scheduleSingle: function (row, fromKeyboard) {
      var a = api();
      if (!a || !row) return false;
      var id = row.getAttribute('data-thread-id');
      if (!id) return false;
      return this.scheduleThread({
        id: id,
        name: row.getAttribute('data-name') || '',
        fromKeyboard: !!fromKeyboard
      });
    },

    /**
     * Delete one request by thread id, undoably. The row is optional: a request
     * deleted from the conversation pane may have no row in the list, and the
     * count alone then carries the optimistic state.
     *
     * Returns whether a window opened. A caller that tore its own UI down for
     * this delete has to tell the user when nothing was sent.
     */
    scheduleThread: function (opts) {
      var a = api();
      if (!a || !opts || !opts.id) return false;
      this.commitNow();

      var capture = captureRow(opts.id);
      // No row in a list that holds the whole queue means the request already
      // left it. Counting it out again would walk the total below the truth.
      // A paginated list is not that evidence: the row may be on a page this
      // view never loaded, and the delete still has to go out.
      if (!capture && typeof a.listComplete === 'function' && a.listComplete()) return false;

      var entry = {
        bulk: false,
        count: 1,
        ids: [String(opts.id)],
        captures: capture ? [capture] : [],
        message: singleMessage(opts.name || ''),
        fromKeyboard: !!opts.fromKeyboard
      };
      afterRemoval(entry);
      start(entry);
      return true;
    },

    /**
     * Delete several requests in one call, undoably. Takes thread id strings.
     * Returns whether a window opened, for the same reason scheduleThread does.
     */
    scheduleBulk: function (ids, fromKeyboard) {
      var a = api();
      if (!a || !ids || !ids.length) return false;
      this.commitNow();

      // Captured in list order, whatever order the rows were picked in, so the
      // neighbours afterRemoval reads bracket the block that went away.
      var ordered = a.rows()
        .map(function (row) { return row.getAttribute('data-thread-id'); })
        .filter(function (id) { return id && ids.indexOf(id) !== -1; });

      var captures = [];
      for (var i = 0; i < ordered.length; i++) {
        var capture = captureRow(ordered[i]);
        if (capture) captures.push(capture);
      }
      if (!captures.length) return false;

      var entry = {
        bulk: true,
        count: captures.length,
        ids: captures.map(function (c) { return c.id; }),
        captures: captures,
        message: bulkMessage(captures.length),
        fromKeyboard: !!fromKeyboard
      };
      afterRemoval(entry);
      start(entry);
      return true;
    },

    /**
     * Take one thread out of the open window, putting its row and count back.
     * Accepting a request whose delete is still pending must not send that
     * delete. The rest of a batch runs its window out unchanged.
     */
    cancelPending: function (threadId) {
      if (!pending) return false;
      var id = String(threadId);
      var i = pending.ids.indexOf(id);
      if (i === -1) return false;
      if (pending.ids.length === 1) {
        settle(pending, true);
        return true;
      }
      pending.ids.splice(i, 1);
      pending.count = Math.max(0, pending.count - 1);
      for (var k = 0; k < pending.captures.length; k++) {
        if (pending.captures[k].id === id) {
          restorePart(pending.captures.splice(k, 1));
          break;
        }
      }
      return true;
    },

    /**
     * Take one thread out of the open window with no send and no restore. For a
     * request the server removed elsewhere: Undo must not bring back a thread
     * that is gone, and its count already left with the local delete.
     */
    forgetPending: function (threadId) {
      if (!pending) return false;
      var id = String(threadId);
      var i = pending.ids.indexOf(id);
      if (i === -1) return false;
      pending.ids.splice(i, 1);
      pending.count = Math.max(0, pending.count - 1);
      pending.captures = pending.captures.filter(function (c) { return c.id !== id; });
      if (!pending.ids.length) {
        var entry = pending;
        entry.settled = true;
        pending = null;
        if (entry.toast && typeof entry.toast.dismiss === 'function') entry.toast.dismiss();
        announceSettled(false);
      }
      return true;
    },

    /**
     * Close the window now and send. `keepalive` keeps the send alive across a
     * page that is going away. `silent` says the failure cannot be acted on,
     * because there will be no page left to restore a row to.
     */
    commitNow: function (options) {
      if (!pending) return;
      var entry = pending;
      if (options && options.keepalive) entry.keepalive = true;
      if (options && options.silent) entry.silent = true;
      settle(entry, false);
    },

    /**
     * Requests taken out of the view whose decline has not been sent yet. The
     * server still counts them, so a count read from the server is this many
     * too high while a window is open.
     */
    pendingCount: function () {
      return pending ? pending.count : 0;
    },

    /**
     * Requests the server still counts, including the ones whose decline is
     * already on the wire. This is the number a count read from the server is
     * too high by, and the number that says the rendered list is not the
     * server's to replace.
     */
    heldCount: function () {
      return (pending ? pending.count : 0) + inFlightCount();
    },

    /**
     * Whether this request is out of the view but still on the server. A page
     * or a refresh that carries it back must drop it rather than render it.
     */
    holdsThread: function (threadId) {
      var id = String(threadId);
      if (pending && pending.ids.indexOf(id) !== -1) return true;
      for (var i = 0; i < inFlightEntries.length; i++) {
        if (inFlightEntries[i].ids.indexOf(id) !== -1) return true;
      }
      return false;
    }
  };

  // A page that goes away mid-window commits rather than dropping the delete:
  // the row is already gone from the view the user is leaving. Nothing can be
  // restored or reported after this, so the send is silent as well.
  window.addEventListener('pagehide', function () {
    window.PmRequestsUndo.commitNow({ keepalive: true, silent: true });
  });
  // A hidden tab can be discarded, so the send has to survive the page. The
  // page usually comes back, so a failure is still restored and reported.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      window.PmRequestsUndo.commitNow({ keepalive: true });
    }
  });
})();
